<script setup lang="ts">
import type { Suggestion, SuggestionVoteResult } from '~~/shared/utils/suggestions'

interface Props {
  suggestion: Suggestion
}

defineProps<Props>()

const emit = defineEmits<{
  voted: [result: SuggestionVoteResult]
}>()
</script>

<template>
  <!-- Stretched-link row: the title link covers the card; the vote control
       and author link sit above it (nested interactive elements are invalid). -->
  <article class="group relative flex gap-4 rounded-2xl border border-default bg-elevated/40 p-4 shadow-(--surface-shadow) transition-colors hover:border-accented sm:p-5">
    <div class="relative z-10">
      <SuggestionVote
        :suggestion-id="suggestion.id"
        :score="suggestion.score"
        :upvotes="suggestion.upvotes"
        :downvotes="suggestion.downvotes"
        :my-vote="suggestion.my_vote"
        @voted="emit('voted', $event)"
      />
    </div>

    <div class="min-w-0 flex-1">
      <NuxtLink
        :to="`/suggestions/${suggestion.id}`"
        class="block text-base font-semibold text-highlighted transition-colors after:absolute after:inset-0 group-hover:text-primary"
      >
        {{ suggestion.title }}
      </NuxtLink>

      <p
        v-if="suggestion.description"
        class="mt-1 line-clamp-2 text-sm text-muted"
      >
        {{ suggestion.description }}
      </p>

      <div class="mt-3 flex flex-wrap items-center gap-x-3 gap-y-2 text-xs text-dimmed">
        <SuggestionBadges
          :category="suggestion.category"
          :status="suggestion.status"
        />
        <span class="flex items-center gap-1">
          <UIcon
            name="i-lucide-message-square"
            class="size-3.5"
          />
          {{ suggestion.comment_count }}
        </span>
        <span>
          <NuxtLink
            v-if="suggestion.author.username"
            :to="`/${suggestion.author.username}`"
            class="relative z-10 hover:text-toned hover:underline"
          >@{{ suggestion.author.username }}</NuxtLink>
          <template v-else>Deleted user</template>
          ·
          <NuxtTime
            :datetime="suggestion.created_at"
            relative
            locale="en-US"
          />
        </span>
      </div>
    </div>
  </article>
</template>
